// Shared shell for the unauthenticated public pages (Transparency,
// Privacy, Terms). Reachable while signed-out so prospective users
// can read the policy text before creating an account (Req 26.1–26.4).

import { Cloud } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@link/ui";

export interface LegalLayoutProps {
  title: string;
  /** Plain "last revised" date string, e.g. "May 24, 2026". */
  revisedAt: string;
  children: ReactNode;
  /** Highlight the active nav row. */
  current: "transparency" | "privacy" | "terms";
}

export function LegalLayout({
  title,
  revisedAt,
  children,
  current,
}: LegalLayoutProps) {
  return (
    <div className="min-h-screen bg-app-canvas text-cloud-700">
      <header className="border-b border-cloud-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-4">
          <a href="/" className="flex items-center gap-2 text-cloud-800">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-strawberry-gradient text-white shadow-sm">
              <Cloud className="h-4 w-4" />
            </span>
            <span className="text-base font-semibold">Link</span>
          </a>
          <nav className="ml-auto flex items-center gap-1 text-sm">
            <NavLink href="/transparency" active={current === "transparency"}>
              Transparency
            </NavLink>
            <NavLink href="/legal/privacy" active={current === "privacy"}>
              Privacy
            </NavLink>
            <NavLink href="/legal/terms" active={current === "terms"}>
              Terms
            </NavLink>
            <a
              href="/"
              className="ml-2 rounded-md bg-strawberry-gradient px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
            >
              Open the app
            </a>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-cloud-500">
          Last revised · {revisedAt}
        </p>
        <h1 className="mt-1 text-3xl font-semibold text-cloud-800">{title}</h1>
        <article className="prose prose-sm prose-headings:text-cloud-800 prose-headings:font-semibold prose-p:text-cloud-700 prose-li:text-cloud-700 prose-strong:text-cloud-800 mt-8 max-w-none">
          {children}
        </article>
        <footer className="mt-12 border-t border-cloud-200 pt-6 text-xs text-cloud-500">
          Link · Key-based identity and private communication.
        </footer>
      </main>
    </div>
  );
}

function NavLink({
  href,
  children,
  active,
}: {
  href: string;
  children: ReactNode;
  active: boolean;
}) {
  return (
    <a
      href={href}
      className={cn(
        "rounded-md px-3 py-1.5 text-cloud-700 hover:bg-cloud-100",
        active && "bg-cloud-100 font-semibold text-cloud-800",
      )}
    >
      {children}
    </a>
  );
}
